import type { CatalogItem } from '../../domain/entities/catalog.ts';

/**
 * Who a part can be bought from (INV2-15, SCRUM-284).
 *
 * **One place that knows about both shapes.** An item written before
 * 2026-10-29 carries a single `vendorContactId` and `vendorItemNumber`;
 * one written since carries a `vendors` list. Every reader goes through
 * here, so none of them can accidentally see half an item's suppliers —
 * which is the failure mode of migrating a field by adding a new one
 * beside it.
 *
 * It also means the backfill is tidying rather than a prerequisite: the
 * product is correct against both shapes today.
 */
export interface VendorLink {
  readonly vendorContactId: string;
  readonly vendorItemNumber?: string;
}

/** Everyone this item can be bought from, old shape or new. */
export function itemVendors(
  item: Pick<CatalogItem, 'vendors' | 'vendorContactId' | 'vendorItemNumber'>,
): VendorLink[] {
  if (item.vendors && item.vendors.length > 0) return [...item.vendors];
  if (!item.vendorContactId) return [];
  return [
    {
      vendorContactId: item.vendorContactId,
      ...(item.vendorItemNumber ? { vendorItemNumber: item.vendorItemNumber } : {}),
    },
  ];
}

/** Whether this item can be bought from that vendor. */
export function itemHasVendor(
  item: Pick<CatalogItem, 'vendors' | 'vendorContactId' | 'vendorItemNumber'>,
  vendorContactId: string,
): boolean {
  return itemVendors(item).some((link) => link.vendorContactId === vendorContactId);
}

/**
 * Every part number this item is known by, for the search to match.
 *
 * **Not simply the links' numbers.** An item can carry a part number
 * with no supplier recorded — the two fields are independent and always
 * have been — and `itemVendors` has nothing to say about such an item
 * because a link without a vendor is not a link. Dropping the number
 * with it would make a part unfindable by the one thing printed on its
 * box, which is exactly what the existing search test caught when this
 * was written the obvious way.
 */
export function itemVendorNumbers(
  item: Pick<CatalogItem, 'vendors' | 'vendorContactId' | 'vendorItemNumber'>,
): string[] {
  const fromLinks = itemVendors(item)
    .map((link) => link.vendorItemNumber)
    .filter((one): one is string => Boolean(one));
  if (fromLinks.length > 0) return fromLinks;
  return item.vendorItemNumber ? [item.vendorItemNumber] : [];
}

/**
 * The list with one vendor's entry put in or replaced (SCRUM-284).
 *
 * **Keyed by vendor**, which is the maintainer's decision: a vendor
 * appears at most once on an item, so giving them a new part number
 * replaces the one that was there rather than appearing beside it. That
 * makes "the same vendor cannot have the same part number twice for the
 * same item" impossible rather than refused — a rule enforced by a
 * shape is one nobody has to remember.
 */
export function withVendor(links: readonly VendorLink[], link: VendorLink): VendorLink[] {
  const others = links.filter((one) => one.vendorContactId !== link.vendorContactId);
  return [...others, link];
}

export function withoutVendor(links: readonly VendorLink[], vendorContactId: string): VendorLink[] {
  return links.filter((one) => one.vendorContactId !== vendorContactId);
}

/**
 * Two part numbers are the same number once case and spacing are set
 * aside — `1g-brkt` and `1G-BRKT ` are one code to everybody but a
 * database. The same reasoning as `tagKey` and `catalogTitleKey`.
 */
export function vendorNumberKey(vendorContactId: string, vendorItemNumber: string): string {
  return `${vendorContactId}:${vendorItemNumber.trim().toLowerCase()}`;
}

/**
 * The items a vendor's part number already points at (SCRUM-284).
 *
 * **The rule that spans documents**: one vendor may not use one part
 * number for two different SKUs, because a number scanned off a box
 * would then be ambiguous. Two *different* vendors sharing a number is
 * fine and common.
 *
 * Checked over the catalog the caller has rather than in a transaction,
 * which is a real limitation and worth naming: two people adding the
 * same number at the same moment could both pass. The same shape as
 * `catalogTitleClashes`, which warns for the same reason — and the
 * airtight version is a callable holding a `vendor:number` index, like
 * the SKU counter. Worth having when a second person edits the catalog
 * at the same time as the first, and not before.
 */
export function vendorNumberClashes(
  items: readonly CatalogItem[],
  link: VendorLink,
  exceptItemId?: string,
): CatalogItem[] {
  if (!link.vendorItemNumber) return [];
  const key = vendorNumberKey(link.vendorContactId, link.vendorItemNumber);
  return items.filter(
    (item) =>
      item.id !== exceptItemId &&
      itemVendors(item).some(
        (one) =>
          one.vendorItemNumber !== undefined &&
          vendorNumberKey(one.vendorContactId, one.vendorItemNumber) === key,
      ),
  );
}
